/**
 * AssessmentDetailPage
 *
 * Detail view for a single income-tax (ITR) assessment:
 *  - Header with FY/AY, ITR type, regime, and status.
 *  - A status timeline (the ITR pipeline) with the current stage highlighted
 *    and an inline status-advance action.
 *  - A tax-calculation panel that calls the assessments service `calculateTax`.
 *
 * Route: /customers/:customerId/assessments/:assessmentId
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Check,
  Calculator,
  AlertCircle,
  FileText,
  Pencil,
  Link2,
  Trash2,
} from 'lucide-react'
import { getAssessmentService } from '@/services/assessments'
import { useCustomerStore } from '@/store/customerStore'
import { toast } from '@/store/toastStore'
import AssessmentModal from '@/components/customers/AssessmentModal'
import RecordFilingModal, {
  RecordFilingFormValues,
} from '@/components/assessments/RecordFilingModal'
import LinkDocumentModal from '@/components/assessments/LinkDocumentModal'
import { ASSESSMENT_STATUS_FLOW, ASSESSMENT_STATUS_LABEL } from '@/types/assessment.types'
import type {
  Assessment,
  AssessmentStatus,
  TaxCalculationResult,
  TaxRegime,
  AssessmentDocument,
} from '@/types/assessment.types'
import { useAuth } from '@/contexts/AuthContext'
import {
  legacyAssessmentReadOnlyMessage,
  legacyAssessmentWritesEnabled,
} from '@/config/assessmentCutover'

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount)
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export default function AssessmentDetailPage() {
  const { hasPermission } = useAuth()
  const canUpdate = legacyAssessmentWritesEnabled && hasPermission('txh.assessment.update')
  const canCalculate = legacyAssessmentWritesEnabled && hasPermission('txh.assessment.calculate')
  const canUpdateStatus =
    legacyAssessmentWritesEnabled && hasPermission('txh.assessment.status.update')
  const canFile = legacyAssessmentWritesEnabled && hasPermission('txh.assessment.file')
  const canApprove = hasPermission('txh.assessment.approve')
  const { customerId, assessmentId } = useParams<{
    customerId: string
    assessmentId: string
  }>()
  const navigate = useNavigate()
  const { getCustomerById, customers, loadCustomers } = useCustomerStore()

  const [assessment, setAssessment] = useState<Assessment | null>(null)
  const [loading, setLoading] = useState(true)
  const [advancing, setAdvancing] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isFilingModalOpen, setIsFilingModalOpen] = useState(false)
  const [isLinkDocOpen, setIsLinkDocOpen] = useState(false)
  const [linkedDocuments, setLinkedDocuments] = useState<AssessmentDocument[]>([])
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null)

  // Tax calc panel state
  const [grossIncome, setGrossIncome] = useState('')
  const [deductions, setDeductions] = useState('')
  const [regime, setRegime] = useState<TaxRegime>('new')
  const [calcResult, setCalcResult] = useState<TaxCalculationResult | null>(null)
  const [calculating, setCalculating] = useState(false)

  const customer = customerId ? getCustomerById(customerId) : undefined

  useEffect(() => {
    if (customers.length === 0) loadCustomers()
  }, [customers.length, loadCustomers])

  const loadLinkedDocs = useCallback(async () => {
    if (!assessmentId) return
    try {
      const docs = await getAssessmentService().listDocuments(assessmentId)
      setLinkedDocuments(docs)
    } catch {
      // Non-fatal
    }
  }, [assessmentId])

  const load = useCallback(async () => {
    if (!assessmentId) return
    setLoading(true)
    try {
      const a = await getAssessmentService().get(assessmentId)
      setAssessment(a)
      setRegime(a.taxRegime ?? 'new')
      if (a.grossIncome != null) setGrossIncome(String(a.grossIncome))
      if (a.totalDeductions != null) setDeductions(String(a.totalDeductions))
      await loadLinkedDocs()
    } catch (err) {
      toast.error(
        'Could not load assessment',
        err instanceof Error ? err.message : 'Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }, [assessmentId, loadLinkedDocs])

  useEffect(() => {
    load()
  }, [load])

  const backToCustomer = () => {
    if (customerId) navigate(`/customers/${customerId}`)
    else navigate('/customers')
  }

  // Determine the next status in the pipeline (if any).
  const currentFlowIndex = assessment ? ASSESSMENT_STATUS_FLOW.indexOf(assessment.status) : -1
  const nextStatus: AssessmentStatus | null =
    currentFlowIndex >= 0 && currentFlowIndex < ASSESSMENT_STATUS_FLOW.length - 1
      ? ASSESSMENT_STATUS_FLOW[currentFlowIndex + 1]
      : null
  const canAdvance = canUpdateStatus && (nextStatus !== 'filed' || (canFile && canApprove))

  const handleAdvance = async () => {
    if (!canAdvance || !assessment || !nextStatus) return
    if (nextStatus === 'filed') {
      setIsFilingModalOpen(true)
      return
    }
    setAdvancing(true)
    try {
      const updated = await getAssessmentService().updateStatus(assessment.id, nextStatus)
      setAssessment(updated)
      toast.success('Status updated', ASSESSMENT_STATUS_LABEL[nextStatus])
    } catch (err) {
      toast.error(
        'Could not update status',
        err instanceof Error ? err.message : 'Please try again.'
      )
    } finally {
      setAdvancing(false)
    }
  }

  const handleFilingSubmit = async (values: RecordFilingFormValues) => {
    if (!assessment) return
    try {
      await getAssessmentService().update(assessment.id, {
        acknowledgeNumber: values.acknowledgeNumber,
        filedDate: values.filedDate,
        refundAmount: values.refundAmount,
        demandAmount: values.demandAmount,
      })
      const updated = await getAssessmentService().updateStatus(assessment.id, 'filed')
      setAssessment(updated)
      toast.success('Status updated', 'Marked as Filed')
    } catch (err) {
      toast.error(
        'Could not complete filing',
        err instanceof Error ? err.message : 'Please try again.'
      )
      throw err
    }
  }

  const handleUnlink = async (docId: string) => {
    if (!assessment) return
    if (!window.confirm('Unlink this document from the assessment?')) return
    setUnlinkingId(docId)
    try {
      await getAssessmentService().unlinkDocument(assessment.id, docId)
      setLinkedDocuments(prev => prev.filter(d => d.documentId !== docId))
      toast.success('Document unlinked')
    } catch (err) {
      toast.error(
        'Could not unlink document',
        err instanceof Error ? err.message : 'Please try again.'
      )
    } finally {
      setUnlinkingId(null)
    }
  }

  const handleCalculate = async () => {
    if (!canCalculate || !assessment) return
    const gross = Number(grossIncome)
    if (!grossIncome || Number.isNaN(gross) || gross < 0) {
      toast.error('Enter gross income', 'A non-negative gross income is required to calculate tax.')
      return
    }
    setCalculating(true)
    try {
      const result = await getAssessmentService().calculateTax({
        grossIncome: gross,
        totalDeductions: deductions ? Number(deductions) : 0,
        assessmentYear: assessment.assessmentYear,
        taxRegime: regime,
      })
      setCalcResult(result)
    } catch (err) {
      toast.error(
        'Tax calculation failed',
        err instanceof Error ? err.message : 'Please try again.'
      )
    } finally {
      setCalculating(false)
    }
  }

  if (loading) {
    return (
      <div className="card card-elevated p-12 text-center">
        <span className="loading loading-spinner loading-sm" />
        <p className="text-text-secondary mt-3">Loading assessment…</p>
      </div>
    )
  }

  if (!assessment) {
    return (
      <div className="card card-elevated p-12 text-center">
        <p className="text-text-secondary">Assessment not found</p>
        <button onClick={backToCustomer} className="btn btn-primary btn-sm mt-4">
          Back to Customer
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {!legacyAssessmentWritesEnabled && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          role="status"
        >
          <strong>Legacy record — read only.</strong> {legacyAssessmentReadOnlyMessage}
        </div>
      )}
      {/* Back */}
      <button
        type="button"
        onClick={backToCustomer}
        className="btn btn-ghost btn-sm flex items-center gap-2 -ml-2"
      >
        <ArrowLeft className="w-4 h-4" />
        {customer ? `Back to ${customer.fullName}` : 'Back to Customer'}
      </button>

      {/* Header Card */}
      <div className="card card-elevated p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <FileText className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-text-primary">
                  {assessment.itrType} · FY {assessment.financialYear}
                </h1>
                <span
                  className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                  title="Legacy tax-assessment record"
                >
                  Legacy
                </span>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  {ASSESSMENT_STATUS_LABEL[assessment.status]}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-text-secondary flex-wrap">
                <span>Assessment Year {assessment.assessmentYear}</span>
                <span className="capitalize">{assessment.taxRegime ?? 'new'} regime</span>
                {assessment.pan && (
                  <code className="font-mono bg-surface-overlay px-2 py-0.5 rounded">
                    {assessment.pan}
                  </code>
                )}
              </div>
            </div>
          </div>

          {canUpdate && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsEditOpen(true)}
                className="btn btn-outline btn-sm flex items-center gap-2"
              >
                <Pencil className="w-4 h-4" />
                Edit
              </button>
            </div>
          )}
        </div>

        {/* Figures */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-surface-overlay">
          <div>
            <p className="text-sm text-text-tertiary">Taxable Income</p>
            <p className="text-lg font-semibold text-text-primary">
              {assessment.taxableIncome != null ? formatCurrency(assessment.taxableIncome) : '—'}
            </p>
          </div>
          <div>
            <p className="text-sm text-text-tertiary">Tax Liability</p>
            <p className="text-lg font-semibold text-text-primary">
              {assessment.totalTaxLiability != null
                ? formatCurrency(assessment.totalTaxLiability)
                : '—'}
            </p>
          </div>
          <div>
            <p className="text-sm text-text-tertiary">Refund</p>
            <p className="text-lg font-semibold text-green-600 dark:text-green-400">
              {assessment.refundAmount != null ? formatCurrency(assessment.refundAmount) : '—'}
            </p>
          </div>
          <div>
            <p className="text-sm text-text-tertiary">Demand</p>
            <p className="text-lg font-semibold text-red-600 dark:text-red-400">
              {assessment.demandAmount != null ? formatCurrency(assessment.demandAmount) : '—'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status timeline */}
        <section className="lg:col-span-2 card card-elevated p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-text-primary">Status Timeline</h2>
            {nextStatus && canAdvance && (
              <button
                type="button"
                onClick={handleAdvance}
                disabled={advancing}
                className="btn btn-primary btn-sm flex items-center gap-2"
              >
                {advancing ? (
                  <>
                    <span className="loading loading-spinner loading-sm" />
                    Updating…
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Mark {ASSESSMENT_STATUS_LABEL[nextStatus]}
                  </>
                )}
              </button>
            )}
          </div>

          <ol className="space-y-0">
            {ASSESSMENT_STATUS_FLOW.map((step, idx) => {
              const isPast = currentFlowIndex >= 0 && idx < currentFlowIndex
              const isCurrent = idx === currentFlowIndex
              const isLast = idx === ASSESSMENT_STATUS_FLOW.length - 1
              return (
                <li key={step} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <span
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                        isPast
                          ? 'bg-green-500 text-white'
                          : isCurrent
                            ? 'bg-primary-600 text-white'
                            : 'bg-surface-overlay text-text-secondary'
                      }`}
                    >
                      {isPast ? <Check className="w-4 h-4" /> : idx + 1}
                    </span>
                    {!isLast && (
                      <span
                        className={`w-0.5 flex-1 min-h-[24px] ${
                          isPast ? 'bg-green-500' : 'bg-surface-overlay'
                        }`}
                      />
                    )}
                  </div>
                  <div className={`pb-6 ${isLast ? 'pb-0' : ''}`}>
                    <p
                      className={`text-sm font-medium ${
                        isCurrent ? 'text-text-primary' : 'text-text-secondary'
                      }`}
                    >
                      {ASSESSMENT_STATUS_LABEL[step]}
                    </p>
                    {isCurrent && (
                      <p className="text-xs text-text-tertiary mt-0.5">Current stage</p>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>

          {currentFlowIndex < 0 && (
            <div className="card card-elevated p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-l-4 border-orange-500">
              <p className="text-sm text-text-secondary flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-orange-600" />
                This assessment is in a terminal state ({ASSESSMENT_STATUS_LABEL[assessment.status]}
                ).
              </p>
            </div>
          )}

          {/* Key dates */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4 border-t border-surface-overlay text-sm">
            <div>
              <p className="text-text-tertiary">Due Date</p>
              <p className="text-text-primary font-medium">{formatDate(assessment.dueDate)}</p>
            </div>
            <div>
              <p className="text-text-tertiary">Filed Date</p>
              <p className="text-text-primary font-medium">{formatDate(assessment.filedDate)}</p>
            </div>
            <div>
              <p className="text-text-tertiary">Ack. Number</p>
              <p className="text-text-primary font-medium">
                {assessment.acknowledgeNumber ? (
                  <code className="font-mono bg-surface-overlay px-1 rounded">
                    {assessment.acknowledgeNumber}
                  </code>
                ) : (
                  '—'
                )}
              </p>
            </div>
          </div>
        </section>

        {/* Tax calculation panel */}
        <aside className="card card-elevated p-6 space-y-4">
          <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary-600" />
            Tax Calculator
          </h2>

          <div>
            <label
              htmlFor="calc-gross"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Gross Income <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">
                ₹
              </span>
              <input
                id="calc-gross"
                type="number"
                min={0}
                value={grossIncome}
                onChange={e => setGrossIncome(e.target.value)}
                placeholder="0"
                className="input input-bordered w-full pl-8"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="calc-deductions"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Total Deductions
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">
                ₹
              </span>
              <input
                id="calc-deductions"
                type="number"
                min={0}
                value={deductions}
                onChange={e => setDeductions(e.target.value)}
                placeholder="0"
                className="input input-bordered w-full pl-8"
              />
            </div>
            <p className="text-xs text-text-tertiary mt-1">
              Deductions only reduce taxable income under the old regime.
            </p>
          </div>

          <div>
            <span className="block text-sm font-medium text-text-primary mb-1">Tax Regime</span>
            <div className="flex gap-2">
              {(['new', 'old'] as TaxRegime[]).map(r => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRegime(r)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition ${
                    regime === r
                      ? 'bg-primary-100 text-primary-700 border-primary-500 dark:bg-primary-900/30 dark:text-primary-400'
                      : 'border-surface-overlay text-text-secondary hover:bg-surface-overlay'
                  }`}
                >
                  {r === 'new' ? 'New' : 'Old'}
                </button>
              ))}
            </div>
          </div>

          {canCalculate && (
            <button
              type="button"
              onClick={handleCalculate}
              disabled={calculating}
              className="btn btn-primary w-full flex items-center justify-center gap-2"
            >
              {calculating ? (
                <>
                  <span className="loading loading-spinner loading-sm" />
                  Calculating…
                </>
              ) : (
                <>
                  <Calculator className="w-4 h-4" />
                  Calculate Tax
                </>
              )}
            </button>
          )}

          {calcResult && (
            <div className="space-y-2 pt-4 border-t border-surface-overlay text-sm">
              <div className="flex items-center justify-between">
                <span className="text-text-secondary">Taxable Income</span>
                <span className="text-text-primary font-medium">
                  {formatCurrency(calcResult.taxableIncome)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-secondary">Tax on Income</span>
                <span className="text-text-primary font-medium">
                  {formatCurrency(calcResult.taxOnIncome)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-secondary">Surcharge</span>
                <span className="text-text-primary font-medium">
                  {formatCurrency(calcResult.surcharge)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-secondary">Health &amp; Education Cess</span>
                <span className="text-text-primary font-medium">
                  {formatCurrency(calcResult.educationCess)}
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-surface-overlay">
                <span className="font-semibold text-text-primary">Total Tax Liability</span>
                <span className="font-bold text-text-primary">
                  {formatCurrency(calcResult.totalTaxLiability)}
                </span>
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* Linked Documents Section */}
      <section className="card card-elevated p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h2 className="text-lg font-semibold text-text-primary">
              Linked Documents ({linkedDocuments.length})
            </h2>
          </div>
          {canUpdate && customer && (
            <button
              type="button"
              onClick={() => setIsLinkDocOpen(true)}
              className="btn btn-outline btn-sm flex items-center gap-2"
            >
              <Link2 className="w-4 h-4" />
              Link Document
            </button>
          )}
        </div>

        {linkedDocuments.length === 0 ? (
          <div className="p-8 text-center border border-dashed border-surface-overlay rounded-lg">
            <FileText className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
            <p className="text-sm font-medium text-text-primary">No documents linked</p>
            <p className="text-xs text-text-secondary mt-1">
              Link documents used for this filing (e.g. Form 16, Bank Statements, Computation).
            </p>
          </div>
        ) : (
          <div className="divide-y divide-surface-overlay border border-surface-overlay rounded-lg overflow-hidden">
            {linkedDocuments.map(doc => (
              <div
                key={doc.id}
                className="p-3.5 flex items-center justify-between gap-3 hover:bg-surface-overlay/30 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {doc.displayName || 'Document'}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-text-secondary mt-0.5">
                      {doc.category && (
                        <span className="bg-surface-overlay px-1.5 py-0.5 rounded text-[10px]">
                          {doc.category}
                        </span>
                      )}
                      <span>Linked {formatDate(doc.linkedAt)}</span>
                    </div>
                  </div>
                </div>

                {canUpdate && (
                  <button
                    type="button"
                    onClick={() => handleUnlink(doc.documentId)}
                    disabled={unlinkingId === doc.documentId}
                    className="btn btn-ghost btn-xs btn-square text-text-tertiary hover:text-red-600"
                    title="Unlink document"
                  >
                    {unlinkingId === doc.documentId ? (
                      <span className="loading loading-spinner loading-xs" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {customer && canUpdate && (
        <AssessmentModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          customerId={customer.id}
          customerPan={assessment.pan ?? customer.pan}
          assessment={assessment}
          onSuccess={updated => setAssessment(updated)}
        />
      )}

      {isFilingModalOpen && (
        <RecordFilingModal
          isOpen={isFilingModalOpen}
          onClose={() => setIsFilingModalOpen(false)}
          onSubmit={handleFilingSubmit}
          initialAcknowledgeNumber={assessment.acknowledgeNumber}
          initialFiledDate={assessment.filedDate}
          initialRefundAmount={assessment.refundAmount}
          initialDemandAmount={assessment.demandAmount}
        />
      )}

      {isLinkDocOpen && customer && (
        <LinkDocumentModal
          isOpen={isLinkDocOpen}
          onClose={() => setIsLinkDocOpen(false)}
          customerId={customer.id}
          assessmentId={assessment.id}
          assessmentYear={assessment.assessmentYear}
          alreadyLinkedDocumentIds={linkedDocuments.map(d => d.documentId)}
          onLinked={loadLinkedDocs}
        />
      )}
    </div>
  )
}
